import { useMemo } from "react";

import { useGetIdentity } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";

import { useMembers } from "@/contexts/member-context";
import { useMetricsRepository } from "@/pages/tina/hooks/use-metrics-repository";
import type { IIdentity } from "@/providers/auth-provider";

import { type DominationData, buildDomination } from "./lib";

export interface DominationState {
  data: DominationData | null;
  youUid: string | null;
  isLoading: boolean;
  isError: boolean;
}

export function useDominationData(): DominationState {
  const repo = useMetricsRepository();
  const { members } = useMembers();
  const { data: identity } = useGetIdentity<IIdentity>();

  const q = useQuery({
    queryKey: ["tina", "metrics", "hitl-domination"],
    queryFn: () => repo.getHitlDomination(),
  });

  // The pace is a per-curator fact from a second read, because it cannot be derived
  // from the domination rows: its divisor is days WORKED, and the daily counts are
  // skewed enough that only a median describes a curator honestly.
  const activityQ = useQuery({
    queryKey: ["tina", "metrics", "curator-activity"],
    queryFn: () => repo.getCuratorActivity(),
  });

  // `new Date()` is read here rather than inside buildDomination so the fold stays
  // pure and testable. It re-reads only when the rows or members change, which is
  // fine for "today": a board left open across midnight refreshes on the next refetch.
  const data = useMemo(
    () =>
      q.data
        ? buildDomination(q.data, activityQ.data ?? [], members, new Date())
        : null,
    [q.data, activityQ.data, members],
  );

  // Loading reflects only the domination query. The members list (curator
  // names/avatars) is an enhancement layered on via useMemo when it arrives —
  // gating on it would spin forever whenever the one-shot members fetch comes
  // back empty (e.g. auth token not yet attached at mount).
  return {
    data,
    youUid: identity?.id ?? null,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
